import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const FinalCTA = ({ onOpenContact }) => {
  const { t } = useLanguage();

  const steps = [
    { 
      number: '01', 
      title: t('finalCta.step1Title'),
      description: t('finalCta.step1Desc')
    },
    { 
      number: '02', 
      title: t('finalCta.step2Title'),
      description: t('finalCta.step2Desc')
    },
    { 
      number: '03', 
      title: t('finalCta.step3Title'),
      description: t('finalCta.step3Desc')
    },
  ];

  return (
    <section 
      data-testid="final-cta-section"
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ 
        background: 'radial-gradient(ellipse at center, #1F4858 0%, #1A3A4A 70%, #162F3D 100%)'
      }}
    >
      {/* Subtle texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-6xl mx-auto px-6 md:px-12 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Heading */}
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-4">
            {t('finalCta.title')}
          </h2>
          <p className="font-body text-lg text-white/60 mb-16 max-w-2xl mx-auto">
            {t('finalCta.subtitle')}
          </p>

          {/* 3-Column Step Layout */}
          <div className="mb-14">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 relative">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="relative"
                >
                  {/* Step Card */}
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 md:p-8 h-full">
                    {/* Step Number */}
                    <span className="font-heading text-4xl md:text-5xl font-bold text-[#60B5AF] mb-4 block">
                      {step.number}
                    </span>
                    {/* Title */}
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">
                      {step.title}
                    </h3>
                    {/* Description */}
                    <p className="font-body text-sm text-white/50 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Arrow connector - only on desktop, between cards */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <ChevronRight className="w-8 h-8 text-[#60B5AF]/40" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Trust Line */}
          <p className="font-body text-sm text-white/40 italic mb-8 max-w-xl mx-auto">
            {t('finalCta.trustLine')}
          </p>

          {/* CTA Button - More prominent */}
          <Button
            onClick={onOpenContact}
            className="bg-[#60B5AF] text-white hover:bg-[#60B5AF]/90 font-body font-semibold rounded-xl px-12 py-7 text-lg group transition-all shadow-lg shadow-[#60B5AF]/20"
            data-testid="final-cta-btn"
          >
            {t('finalCta.cta')}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>

          {/* Subtext below button */}
          <p className="font-body text-xs text-white/30 mt-5">
            {t('finalCta.noCommitment')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
